import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import store from '../../store';
import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../store/modules/overview-panel/types';

import DataTable from '../core/charts/Datatable';
import dataBoard from '../core/databoard';
import filters from '../core/filters';
import worldMap from '../core/map';

import ColorBrewer from '../core/tools/colorbrewer';
import translate from '../core/tools/translate';
import environment from '../environment';
import { isNationalArea } from '../shared/validation';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';

import bootbox from 'bootbox';
import { FeaturePermission } from '../../constants/feature.permission.helper';
import { ContextEnum } from '../../enuns/context.enum';
import { tryOrReturn } from "../../utils";
import api from '../api';
import Storage from '../storage';

const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class DirectSalesControl extends CoreControl {
    constructor() {
        super({
            name: 'DirectSales'
        });

        this.select = this.select.bind(this);
        this._change = this._change.bind(this);
        this._changePeriod = this._changePeriod.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this.setFillColor = this.setFillColor.bind(this);
        this.toggleOverview = this.toggleOverview.bind(this);
        this._changeZoomLevel = this._changeZoomLevel.bind(this);
    }

    static getInstance() {
        if (!DirectSalesControl._instance) {
            DirectSalesControl._instance = new DirectSalesControl();
        }

        return DirectSalesControl._instance;
    }

    init() {
        const values = {
            help: translate(`Help.${this.options.name}`),
            id: 'layer-direct-sales',
            input: 'checkbox',
            label: translate(`Common.DirectSales`),
            layer: this.getLayer,
            change: this._change,
            fillColor: {
                name: 'fillColor',
                change: this.setFillColor
            },
            name: this.options.name,
            slider: 'range',
            type: 'vector-cluster-heat-area',
            overview: this.toggleOverview,
            tab() {
                return $('#direct-sales-tab-link');
            },
            filter: this.createFilter,
            dropdown: {
                name: 'list',
                dataSource: _.map(environment.get('CONFIG.FILTERS.DIRECT_TYPES'), t => ({
                    text: translate(t.name),
                    value: t.id
                })),
                change: this.select
            }
        };

        values.links = {};
        const formatDate = (date: Date): string => moment(date).format('MMM/YYYY');
        const { start, end } = this.getDate();

        values.links[translate('Action.ShowDateFrom')] = {
            id: 'layer-direct-sales-date',
            name: 'DirectSalesByYear',
            text: `${formatDate(start)} -> ${formatDate(end)}`,
            value: {
                start: start.toDate(),
                end: end.toDate(),
                max: environment.get('CONFIG.FILTERS.DIRECTSALES_ENDDATE'),
                min: environment.get('CONFIG.FILTERS.DIRECTSALES_STARTDATE')
            },
            change: this._changePeriod,
            type: 'datepicker'
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    select(value, text) {
        if (_.isString(value)) {
            value = Number(value);
        }

        this.getLayer().options.remote.params.locationType = value;

        if (this.isNationalArea(value, text)) {
            this.getLayer().options.scaleRange.setOptions({
                id: value,
                type: 'SelectedAreas',
                property: 'locationType'
            });
        } else {
            this.getLayer().options.scaleRange.setOptions({
                id: value,
                type: 'MarkedArea',
                property: 'locationType'
            });
        }

        if (this.check()) {
            this.getLayer().redraw();
        }
    }

    updateLegend(areaName) {
        return this.getLayer().setOptions({
            legend: {
                title: areaName
                    ? translate('Common.DirectSales', {
                        location: areaName
                    })
                    : translate('Common.UnmetDemandRetail'),
                subtitle: `${this.getDate().start.format('MM/YYYY')} - ${this.getDate().end.format('MM/YYYY')}`
            }
        });
    }

    _changePeriod(date, id = this.getLayer().getMap()._id) {
        this.date.set(id, {
            start: moment(date.start),
            end: moment(date.end)
        });

        api.general.log.action(ContextEnum.LayerDirectSale, 'Mudou Período', JSON.stringify(this.date));

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().redraw();
        }
    }
    _changeZoomLevel(layer = this.getLayer()) {
        bootbox.dialog({
            title: '<i class="fa fa-warning"></i> Aviso',
            message: 'Ir para nível de zoom adequado da camada selecionada',
            backdrop: false,
            buttons: {
                main: {
                    label: '<i class="fa fa-times"></i> Cancelar',
                    className: 'btn-default'
                },
                danger: {
                    label: '<i class="fa fa-check"></i> Ir',
                    className: 'btn-success',
                    callback: () => {
                        this.getLayer()
                            .getMap()
                            .setZoom(11);
                        setTimeout(() => layer.redraw(), 1000);
                    }
                }
            }
        });
    }

    _change(e) {
        api.general.log.action(ContextEnum.LayerDirectSale, e.checked ? 'enable' : 'disable');
        const layer = this.getLayer();
        const type = Number(this.getControls().dropdown.list.value);
        if (_.isEqual(type, 3) && e.checked) {
            this._changeZoomLevel();
        } else {
            if (e.checked) {
                if (!layer.isEmpty() && layer.samePosition()) {
                    layer.reload();
                } else {
                    layer.redraw();
                }
            } else {
                layer.hide();
                // LayerInfo(this.options.name).reset();
                // this.show({ empty: true });
            }
        }
        this._setFilterButtonVisibility(e.checked);
    }

    _setFilterButtonVisibility(state: boolean) {
        try {
            this.getControls().button.filter.input.enable(state);
            this.getControls().button.filter.enable = state;

            if (state) {
                this.createFilter();
            }

            if (!state) {
                filters.close();
            }
        } catch (err) {
            console.log('ERROR: ', err);
        }

        try {
            this.getControls().button.overview.input.enable(state);
            this.getControls().button.overview.enable = state;
            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === this.options.name && !state) {
                dataBoard.hide();
                delete this._overviewFeatures;
            }
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }

    getType() {
        const { params } = this.getLayer().options.remote;
        return params.locationType;
    }

    getTypeValue() {
        return this.getType();
    }

    getDate(id = this.getLayer().getMap()._id) {
        if (_.isUndefined(this.date)) {
            this.date = new Map();
        }

        if (!this.date.has(id)) {
            this.date.set(
                id,
                id === 0
                    ? {
                        start: moment(environment.get('CONFIG.FILTERS.ENDYEAR')).startOf('year'),
                        end: environment.validate.date.end(
                            moment(environment.get('CONFIG.FILTERS.ENDYEAR')).endOf('year')
                        )
                    }
                    : this._date.get(id - 1)
            );
        }

        return this.date.get(id);
    }

    createFilter() {
        const currentMap = worldMap.getCurrent();
        filters.create({
            container: document.getElementById('filter-container'),
            id: `filters-${currentMap._id}`,
            label: translate(`Common.${this.options.name}`),
            type: 'directSales',
            direction: 'up',
            onSubmit: this._filterCallback,
            fields: {
                segments: true,
                brands: true,
                channels: true,
                companySizes: true,
                fuelTypes: true,
            }
        });

        filters.set(`filters-${currentMap._id}`, currentMap._id, {
            channels: {},
            brands: {},
            models: {},
            group: {},
            dealer: {},
            fuelTypes: {},
            industry: {},
        });
    }

    // trigger event when filtered data
    _filterCallback() {
        //if (this.check() && this.getLayer().visible()) {

        api.general.log.action(ContextEnum.LayerDirectSale, 'Mudança Filtro');
        if (this.check()) {
            this.getLayer().redraw();
        }
    }

    isNationalArea(value, text) {
        const dropDownText = tryOrReturn(() => this.getControls().dropdown.list.text, '');
        const dropDownValue = tryOrReturn(() => Number(this.getControls().dropdown.list.value), -1);

        return isNationalArea(value || dropDownValue, text || dropDownText);
    }

    setFillColor(value) {
        // console.time('NeoGIG.Feature[this.options.name] -> setFillColor');

        const color = value.split(':')[0];
        const index = value.split(':')[1];

        const { symbology } = this.getLayer().options;
        symbology.heat.theme = ColorBrewer.getHeatColors(color);
        symbology.cluster.theme = ColorBrewer[color][index];
        symbology.style.highlight = ColorBrewer[color][index][Number(index) - 1];

        this.getLayer().setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reload();
        }
        // console.timeEnd('NeoGIG.Feature[this.options.name] -> setFillColor');
    }

    toggleOverview() {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);

        const features = layer.getCurrentFeatures();

        if (!store.getters[OVERVIEW_PANEL_IS_VISIBLE] || !_.isEqual(this._overviewFeatures, features))
            api.general.log.action(ContextEnum.LayerDirectSale, 'Visualizar Gráfico');

        if (
            (_.size(features) && !_.isEqual(this._overviewFeatures, features)) ||
            !store.getters[OVERVIEW_PANEL_IS_VISIBLE]
        ) {
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);

            store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
        }
    }

    summary(features) {
        const data = this.calculate(features);

        const dataStructure = {
            row: translate('Common.Name'),
            column: [translate('Common.Total')]
        };

        const chartPie = new DataTable(data.perPercentage, dataStructure);
        const chartPieSecond = new DataTable(data.perSegments, dataStructure);
        const chartColumn = new DataTable(data.perTotal, dataStructure);

        const date = this.getDate();

        const exportCustomButton = {
            icon: 'download',
            text: 'custom',
            name: translate('Action.Export'),
            callback: async function (mapId = worldMap.getCurrent()._id) {
                let options = Storage.get(`options-directSales-${mapId}`);
                await api.themes.get.areas.directSalesExport(options);
            }
        };

        const options = {
            title: {
                table: `${translate(`Common.${this.options.name}`)} - ${translate("Common.Summary")} | ${date.start.format(
                    'MM/YYYY'
                )} - ${date.end.format('MM/YYYY')}`,
                chartPie: `${translate('Common.RankingDirectSalesPerBrand').toUpperCase()} (%)`,
                chartColumn: translate('Common.RankingDirectSalesPerChannel').toUpperCase(),
                chartPieSecond: `${translate('Common.SegmentRanking')} (%)`
            },
            titleHeader: true,
            titleHeaderMessage: `${translate("Common.Summary")} ${translate('Common.DirectSales')} | ${date.start.format('MM/YYYY')} - ${date.end.format(
                'MM/YYYY'
            )}`,
            dash: true,
            fourthLevel: true,
            data: [data.summary, chartPie, chartColumn, chartPieSecond],
            colors: [_.map(data.perPercentage, c => c.color), _.map(data.perTotal, c => c.color)],
            format: ['{0:n0}', '{0:p1}', '{0:n0}', '{0:p1}'],
            convertTable: false,
            columns: [
                {
                    field: 'name',
                    title: translate('Common.Name')
                },
                {
                    field: 'total',
                    title: translate('Common.Total'),
                    width: 100,
                    template: data => numeral(data.total).format('0,0')
                },
                {
                    field: 'percentage',
                    title: `${translate('Common.Total')} (%)`,
                    width: 100,
                    template: data => numeral(data.percentage).format('0,0[.]00%')
                }
            ],
            buttons: {
                fullscreen: true,
                custom: isAuthorized(FeaturePermission.LAYER_DIRECT_SALE.exportDataPanel) ? exportCustomButton : undefined
            }
        };

        dataBoard.show(options);
    }

    calculate(features) {
        // const filterId = `${_.kebabCase(this.options.name)}-filter-id`;
        const colors = [
            '#FF5722',
            '#9C27B0',
            '#3F51B5',
            '#03A9F4',
            '#009688',
            '#8BC34A',
            '#FFC107',
            '#f44336',
            '#673AB7',
            '#2196F3',
            '#00BCD4',
            '#4CAF50',
            '#CDDC39',
            '#FF9800',
            '#E91E63'
        ];

        const colorByChannel = _.reduce(
            environment.get('CONFIG.FILTERS.SALES_CHANNEL'),
            (acc, val, idx) => {
                acc[val.value] = colors[idx % colors.length];
                return acc;
            },
            {}
        );

        const flattenData = _(features)
            .map(f => f.properties.DirectSales)
            .flatten();

        const total = _.sumBy(features, f => f.properties.Value);
        const summary = _(features)
            .orderBy(f => f.properties.Value)
            .reverse()
            .map(f => ({
                name: f.properties.Nome,
                total: f.properties.Value,
                percentage: f.properties.Value / total
            }))
            .value();

        const perPercentage = flattenData
            .groupBy(f => f.nameBrand)
            .map((f, key) => {
                return {
                    id: f[0].idStoreClient,
                    value: _.sumBy(f, 'qtdSales'),
                    percent: _.sumBy(f, 'qtdSales') / total,
                    text: key,
                    textTruncate: _.truncate(key, {
                        length: 17
                    }),
                    color: colorByChannel[f[0].idBrand]
                };
            })
            .orderBy(f => f.value)
            .reverse()
            .map(f => ({
                ...f,
                [translate('Common.Name')]: f.text,
                [translate('Common.Total')]: f.percent
            }))
            // .take(10)
            .value();

        const perTotal = flattenData
            .groupBy(f => f.nameSaleChannelAggregate)
            .map((f, key) => {
                return {
                    id: f[0].idStoreClient,
                    value: _.sumBy(f, 'qtdSales'),
                    percent: _.sumBy(f, 'qtdSales') / total,
                    text: key,
                    textTruncate: _.truncate(key, {
                        length: 17
                    }),
                    color: colorByChannel[f[0].idSaleChannelAggregate]
                };
            })
            .orderBy(f => f.value)
            .reverse()
            .map(f => ({
                ...f,
                [translate('Common.Name')]: f.text,
                [translate('Common.Total')]: f.value
            }))
            // .take(10)
            .value();

        const perSegments = flattenData
            .filter(f => !!f.idSegment)
            .groupBy(f => f.idSegment)
            .map((f, key) => ({
                id: key,
                text: f[0].nameSegment,
                value: _.sumBy(f, 'qtdSales') / total
            }))
            .orderBy(f => f.value)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.text,
                [translate('Common.Total')]: f.value
            }))
            .value();

        return {
            perSegments,
            perPercentage,
            perTotal,
            summary
        };
    }
}

export default DirectSalesControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/DirectSalesControl.js